import React from 'react';
import { get, forEach, filter, flow, map, toString, isEmpty, includes } from 'lodash/fp';
import { mapWithKeys } from 'utils/lodashUtils';
import { ISidebarRiskPickerMenuItem } from 'components/sidebarRiskPicker/SidebarRiskPickerListContent';
import RiskPickerListItem from './RiskPickerListItem';
import {
    FGAccordion,
    FGAccordionItem,
    FGAccordionItemHeader,
    FGAccordionItemContents,
} from '@fg/design-system';
import {
    RiskPickerAbstractContentProps,
    RiskPickerItem,
    RiskPickerTreeItem,
} from './RiskPicker';
import cx from 'classnames';

type Props = RiskPickerAbstractContentProps & {
    treeMenuItem: RiskPickerTreeItem;
    onItemClick: (id: number) => void;
    multi?: boolean;
};

const RiskPickerTree = React.memo((props: Props) => {
    //todo: memo()
    const renderTree = (
        tree: RiskPickerTreeItem,
        depth: number,
        maxDepth: number,
        childIndex: number
    ) => {
        const isSelected = includes(tree.id, props.selectedItemsIds);
        return (
            <FGAccordion iconAlignment="left" preExpanded={[toString(tree.id)]}>
                <FGAccordionItem uuid={toString(tree.id)}>
                    <FGAccordionItemHeader
                        className={cx('risk-accordion-header', {
                            'no-children': isEmpty(tree.children),
                            [`depth-${depth}`]: true,
                            'selected': isSelected,
                        })}>
                        <RiskPickerListItem
                            isSelected={isSelected}
                            isMultiSelect={props.multi}
                            item={tree}
                            onClick={id => props.onItemClick(id)}
                            riskStructure="tree"
                        />
                    </FGAccordionItemHeader>
                    <FGAccordionItemContents>
                        {mapWithKeys(
                            (childTree: RiskPickerItem, index: number) => (
                                <React.Fragment key={childTree.id}>
                                    {renderTree(
                                        childTree,
                                        depth + 1,
                                        maxDepth,
                                        index
                                    )}
                                </React.Fragment>
                            ),
                            get('children', tree)
                        )}
                    </FGAccordionItemContents>
                </FGAccordionItem>
            </FGAccordion>
        );
    };

    const filterTree = (tree: RiskPickerTreeItem): RiskPickerTreeItem => {
        return {
            ...tree,
            children: flow(
                filter(
                    (childTree: ISidebarRiskPickerMenuItem) => !childTree.hidden
                ),
                map((childTree: ISidebarRiskPickerMenuItem) =>
                    filterTree(childTree)
                )
            )(tree.children),
        };
    };

    //https://stackoverflow.com/questions/16075664/how-to-get-the-total-depth-of-an-unknown-json-hierarchy
    const getMaxDepth = (tree: RiskPickerTreeItem): number => {
        let depth = 0;
        forEach((childTree: RiskPickerTreeItem) => {
            const childDepth = getMaxDepth(childTree);
            if (childDepth > depth) {
                depth = childDepth;
            }
        }, get('children', tree));

        return depth + 1;
    };

    const filteredTree = filterTree(props.treeMenuItem);
    return renderTree(filteredTree, 0, getMaxDepth(filteredTree) - 1, 0);
});

export default RiskPickerTree;
